import {
  DEFAULT_PORTS,
  LoadBalancerServiceInput,
  StatusCodePattern,
  type Certificate,
  type LoadBalancer,
  type LoadBalancerProtocol,
  type LoadBalancerService,
} from "@cloudserver/contracts";
import { Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ApiProblem } from "../api/client.js";
import { useAddService, useCertificates, useUpdateService } from "../api/load-balancers.js";
import { ErrorMessage } from "../ui/feedback.js";
import { FieldHint } from "../ui/help.js";
import { Dialog } from "../ui/overlay.js";
import { Alert, Button, TextField } from "../ui/primitives.js";
import { useToast } from "../ui/toast.js";
import { SelectField } from "./networks.js";

/*
 * The service editor: "listen on port A, forward to port B of the targets",
 * the protocol and its HTTP options, certificates for HTTPS and the health
 * check. Shared by the create page and the balancer's services tab.
 */

/** What the editor holds: text fields as typed, so a half-typed port is not lost. */
export interface ServiceDraft {
  protocol: LoadBalancerProtocol;
  listenPort: string;
  destinationPort: string;
  proxyProtocol: boolean;
  stickySessions: boolean;
  cookieName: string;
  cookieLifetime: string;
  redirectHttp: boolean;
  certificateIds: string[];
  checkProtocol: LoadBalancerProtocol;
  checkPort: string;
  interval: string;
  timeout: string;
  retries: string;
  path: string;
  domain: string;
  statusCodes: string;
  response: string;
  tls: boolean;
}

export function draftOf(service?: LoadBalancerService): ServiceDraft {
  if (!service) {
    const ports = DEFAULT_PORTS.http;
    return {
      protocol: "http",
      listenPort: String(ports.listen),
      destinationPort: String(ports.destination),
      proxyProtocol: false,
      stickySessions: false,
      cookieName: "",
      cookieLifetime: "",
      redirectHttp: false,
      certificateIds: [],
      checkProtocol: "http",
      checkPort: String(ports.destination),
      interval: "15",
      timeout: "10",
      retries: "3",
      path: "/",
      domain: "",
      statusCodes: "2??, 3??",
      response: "",
      tls: false,
    };
  }
  const check = service.healthCheck;
  return {
    protocol: service.protocol,
    listenPort: String(service.listenPort),
    destinationPort: String(service.destinationPort),
    proxyProtocol: service.proxyProtocol,
    stickySessions: service.http?.stickySessions ?? false,
    cookieName: service.http?.cookieName ?? "",
    cookieLifetime: service.http?.cookieLifetimeSeconds?.toString() ?? "",
    redirectHttp: service.http?.redirectHttp ?? false,
    certificateIds: service.certificates.flatMap((c) => (c.id ? [c.id] : [])),
    checkProtocol: check.protocol,
    checkPort: String(check.port),
    interval: String(check.intervalSeconds),
    timeout: String(check.timeoutSeconds),
    retries: String(check.retries),
    path: check.http?.path ?? "/",
    domain: check.http?.domain ?? "",
    statusCodes: (check.http?.statusCodes ?? ["2??", "3??"]).join(", "),
    response: check.http?.response ?? "",
    tls: check.http?.tls ?? false,
  };
}

const port = (text: string) => {
  const n = Number(text.trim());
  return Number.isInteger(n) && n >= 1 && n <= 65_535 ? n : null;
};
const whole = (text: string) => {
  const n = Number(text.trim());
  return text.trim() !== "" && Number.isInteger(n) && n >= 0 ? n : null;
};

/** Per-field problems of a draft (translation keys), empty when it can be sent. */
export function draftErrors(d: ServiceDraft): Partial<Record<keyof ServiceDraft, string>> {
  const errors: Partial<Record<keyof ServiceDraft, string>> = {};
  if (port(d.listenPort) === null) errors.listenPort = "loadBalancers.service.portInvalid";
  if (port(d.destinationPort) === null)
    errors.destinationPort = "loadBalancers.service.portInvalid";
  if (port(d.checkPort) === null) errors.checkPort = "loadBalancers.service.portInvalid";
  if (!whole(d.interval)) errors.interval = "loadBalancers.service.secondsInvalid";
  if (!whole(d.timeout)) errors.timeout = "loadBalancers.service.secondsInvalid";
  if (whole(d.retries) === null) errors.retries = "loadBalancers.service.retriesInvalid";
  if (d.protocol === "https" && d.certificateIds.length === 0)
    errors.certificateIds = "loadBalancers.service.certificateNeeded";
  if (d.checkProtocol !== "tcp") {
    if (!d.path.startsWith("/")) errors.path = "loadBalancers.service.pathInvalid";
    const codes = codesOf(d.statusCodes);
    if (codes.length === 0 || codes.some((c) => !StatusCodePattern.test(c)))
      errors.statusCodes = "loadBalancers.service.codesInvalid";
  }
  if (d.cookieLifetime.trim() !== "" && !whole(d.cookieLifetime))
    errors.cookieLifetime = "loadBalancers.service.secondsInvalid";
  return errors;
}

const codesOf = (text: string) =>
  text
    .split(/[\s,]+/)
    .map((c) => c.trim())
    .filter(Boolean);

/** The request a valid draft makes. */
export function inputOf(d: ServiceDraft): LoadBalancerServiceInput {
  const web = d.protocol !== "tcp";
  return {
    protocol: d.protocol,
    listenPort: Number(d.listenPort),
    destinationPort: Number(d.destinationPort),
    proxyProtocol: d.proxyProtocol,
    healthCheck: {
      protocol: d.checkProtocol,
      port: Number(d.checkPort),
      intervalSeconds: Number(d.interval),
      timeoutSeconds: Number(d.timeout),
      retries: Number(d.retries),
      http:
        d.checkProtocol === "tcp"
          ? null
          : {
              domain: d.domain.trim() || null,
              path: d.path.trim(),
              response: d.response.trim() || null,
              statusCodes: codesOf(d.statusCodes),
              tls: d.tls,
            },
    },
    http: web
      ? {
          stickySessions: d.stickySessions,
          cookieName: d.stickySessions && d.cookieName.trim() ? d.cookieName.trim() : null,
          cookieLifetimeSeconds:
            d.stickySessions && d.cookieLifetime.trim() ? Number(d.cookieLifetime) : null,
          redirectHttp: d.protocol === "https" && d.redirectHttp,
          certificateIds: d.protocol === "https" ? d.certificateIds : [],
        }
      : null,
  };
}

/** True when the draft passes our checks and the shared contract. */
export const draftValid = (d: ServiceDraft) =>
  Object.keys(draftErrors(d)).length === 0 &&
  LoadBalancerServiceInput.safeParse(inputOf(d)).success;

function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex min-h-6 cursor-pointer items-start gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4 accent-accent"
      />
      <span className="flex flex-col">
        <span className="font-medium">{label}</span>
        {hint && <span className="text-xs text-muted">{hint}</span>}
      </span>
    </label>
  );
}

/** The fields of one service; `fixedPort` keeps the listen port of an existing service. */
export function ServiceFields({
  projectId,
  draft,
  onChange,
  showErrors,
  fixedPort = false,
  certificates,
}: {
  projectId: string;
  draft: ServiceDraft;
  onChange: (next: ServiceDraft) => void;
  showErrors: boolean;
  fixedPort?: boolean;
  certificates: Certificate[];
}) {
  const { t } = useTranslation();
  const [advanced, setAdvanced] = useState(false);
  const errors = showErrors ? draftErrors(draft) : {};
  const err = (key: keyof ServiceDraft) => (errors[key] ? t(errors[key]) : undefined);
  const set = (change: Partial<ServiceDraft>) => onChange({ ...draft, ...change });
  const setProtocol = (protocol: LoadBalancerProtocol) => {
    const ports = DEFAULT_PORTS[protocol];
    const fromDefaults = DEFAULT_PORTS[draft.protocol];
    // Ports still at the old protocol's defaults follow the new one.
    const listen =
      !fixedPort && draft.listenPort === String(fromDefaults.listen)
        ? String(ports.listen)
        : draft.listenPort;
    const destination =
      draft.destinationPort === String(fromDefaults.destination)
        ? String(ports.destination)
        : draft.destinationPort;
    set({
      protocol,
      listenPort: listen,
      destinationPort: destination,
      checkProtocol:
        protocol === "tcp" ? "tcp" : draft.checkProtocol === "tcp" ? "http" : draft.checkProtocol,
      checkPort: draft.checkPort === draft.destinationPort ? destination : draft.checkPort,
    });
  };
  const setDestination = (value: string) =>
    set({
      destinationPort: value,
      checkPort: draft.checkPort === draft.destinationPort ? value : draft.checkPort,
    });
  const web = draft.protocol !== "tcp";
  return (
    <div className="flex flex-col gap-4">
      <SelectField
        label={t("loadBalancers.service.protocol")}
        name="protocol"
        value={draft.protocol}
        onChange={(v) => setProtocol(v as LoadBalancerProtocol)}
        hint={t(`loadBalancers.service.protocolHints.${draft.protocol}`)}
      >
        {(["http", "https", "tcp"] as const).map((p) => (
          <option key={p} value={p}>
            {p.toUpperCase()}
          </option>
        ))}
      </SelectField>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label={t("loadBalancers.service.listenPort")}
          name="listenPort"
          inputMode="numeric"
          value={draft.listenPort}
          onChange={(e) => set({ listenPort: e.target.value })}
          disabled={fixedPort}
          hint={
            fixedPort
              ? t("loadBalancers.service.listenFixed")
              : t("loadBalancers.service.listenHint")
          }
          error={err("listenPort")}
        />
        <TextField
          label={t("loadBalancers.service.destinationPort")}
          name="destinationPort"
          inputMode="numeric"
          value={draft.destinationPort}
          onChange={(e) => setDestination(e.target.value)}
          hint={t("loadBalancers.service.destinationHint")}
          error={err("destinationPort")}
        />
      </div>
      <p className="text-sm text-muted">
        {t("loadBalancers.service.forwardSummary", {
          listen: draft.listenPort || "…",
          destination: draft.destinationPort || "…",
        })}
      </p>
      {draft.protocol === "https" && (
        <fieldset className="flex flex-col gap-2 text-sm">
          <legend className="mb-1 flex items-center gap-1.5 font-medium">
            {t("loadBalancers.service.certificates")}
            <FieldHint text={t("loadBalancers.service.certificatesHint")} topic="certificates" />
          </legend>
          {certificates.length === 0 ? (
            <Alert tone="warn">
              {t("loadBalancers.service.noCertificates")}{" "}
              <Link
                to="/projects/$projectId/certificates"
                params={{ projectId }}
                className="font-medium underline"
              >
                {t("loadBalancers.service.openCertificates")}
              </Link>
            </Alert>
          ) : (
            certificates.map((c) => (
              <label key={c.id} className="flex min-h-6 items-center gap-2">
                <input
                  type="checkbox"
                  className="size-4 accent-accent"
                  checked={draft.certificateIds.includes(c.id)}
                  onChange={(e) =>
                    set({
                      certificateIds: e.target.checked
                        ? [...draft.certificateIds, c.id]
                        : draft.certificateIds.filter((id) => id !== c.id),
                    })
                  }
                />
                <span className="font-medium">{c.name}</span>
                <span className="truncate text-xs text-muted">{c.domains.join(", ")}</span>
              </label>
            ))
          )}
          {err("certificateIds") && <p className="text-xs text-danger">{err("certificateIds")}</p>}
        </fieldset>
      )}
      <div className="flex flex-col gap-3">
        {draft.protocol === "https" && (
          <Toggle
            label={t("loadBalancers.service.redirectHttp")}
            hint={t("loadBalancers.service.redirectHttpHint")}
            checked={draft.redirectHttp}
            onChange={(redirectHttp) => set({ redirectHttp })}
          />
        )}
        {web && (
          <Toggle
            label={t("loadBalancers.service.sticky")}
            hint={t("loadBalancers.service.stickyHint")}
            checked={draft.stickySessions}
            onChange={(stickySessions) => set({ stickySessions })}
          />
        )}
        <Toggle
          label={t("loadBalancers.service.proxyProtocol")}
          hint={t("loadBalancers.service.proxyProtocolHint")}
          checked={draft.proxyProtocol}
          onChange={(proxyProtocol) => set({ proxyProtocol })}
        />
      </div>
      <section className="flex flex-col gap-3 rounded-lg border border-line p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* Inline, not flex: on a 320 px phone the hint moves under the title, not a word. */}
          <h3 className="text-sm font-semibold">
            {t("loadBalancers.service.healthCheck")}{" "}
            <span className="inline-flex align-middle">
              <FieldHint text={t("loadBalancers.service.healthCheckHint")} topic="load-balancers" />
            </span>
          </h3>
          <Button variant="secondary" onClick={() => setAdvanced((v) => !v)}>
            {advanced ? t("loadBalancers.service.lessCheck") : t("loadBalancers.service.moreCheck")}
          </Button>
        </div>
        <p className="text-xs text-muted">
          {t("loadBalancers.service.checkSummary", {
            protocol: draft.checkProtocol.toUpperCase(),
            port: draft.checkPort,
            path: draft.checkProtocol === "tcp" ? "" : draft.path,
            interval: draft.interval,
          })}
        </p>
        {advanced && (
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label={t("loadBalancers.service.checkProtocol")}
              name="checkProtocol"
              value={draft.checkProtocol}
              onChange={(v) => set({ checkProtocol: v as LoadBalancerProtocol })}
            >
              {/* Hetzner documents tcp and http checks; https is kept only when a balancer has it. */}
              {(
                ["http", "tcp", ...(draft.checkProtocol === "https" ? ["https"] : [])] as const
              ).map((p) => (
                <option key={p} value={p}>
                  {p.toUpperCase()}
                </option>
              ))}
            </SelectField>
            <TextField
              label={t("loadBalancers.service.checkPort")}
              name="checkPort"
              inputMode="numeric"
              value={draft.checkPort}
              onChange={(e) => set({ checkPort: e.target.value })}
              error={err("checkPort")}
            />
            <TextField
              label={t("loadBalancers.service.interval")}
              name="interval"
              inputMode="numeric"
              value={draft.interval}
              onChange={(e) => set({ interval: e.target.value })}
              error={err("interval")}
            />
            <TextField
              label={t("loadBalancers.service.timeout")}
              name="timeout"
              inputMode="numeric"
              value={draft.timeout}
              onChange={(e) => set({ timeout: e.target.value })}
              error={err("timeout")}
            />
            <TextField
              label={t("loadBalancers.service.retries")}
              name="retries"
              inputMode="numeric"
              value={draft.retries}
              onChange={(e) => set({ retries: e.target.value })}
              hint={t("loadBalancers.service.retriesHint")}
              error={err("retries")}
            />
            {draft.checkProtocol !== "tcp" && (
              <>
                <TextField
                  label={t("loadBalancers.service.path")}
                  name="path"
                  value={draft.path}
                  onChange={(e) => set({ path: e.target.value })}
                  spellCheck={false}
                  error={err("path")}
                />
                <TextField
                  label={t("loadBalancers.service.domain")}
                  name="domain"
                  value={draft.domain}
                  onChange={(e) => set({ domain: e.target.value })}
                  spellCheck={false}
                  hint={t("loadBalancers.service.domainHint")}
                />
                <TextField
                  label={t("loadBalancers.service.statusCodes")}
                  name="statusCodes"
                  value={draft.statusCodes}
                  onChange={(e) => set({ statusCodes: e.target.value })}
                  spellCheck={false}
                  hint={t("loadBalancers.service.statusCodesHint")}
                  error={err("statusCodes")}
                />
                <TextField
                  label={t("loadBalancers.service.response")}
                  name="response"
                  value={draft.response}
                  onChange={(e) => set({ response: e.target.value })}
                  hint={t("loadBalancers.service.responseHint")}
                />
                <Toggle
                  label={t("loadBalancers.service.tls")}
                  hint={t("loadBalancers.service.tlsHint")}
                  checked={draft.tls}
                  onChange={(tls) => set({ tls })}
                />
              </>
            )}
            {web && draft.stickySessions && (
              <>
                <TextField
                  label={t("loadBalancers.service.cookieName")}
                  name="cookieName"
                  value={draft.cookieName}
                  onChange={(e) => set({ cookieName: e.target.value })}
                  spellCheck={false}
                  placeholder="HCLBSTICKY"
                  hint={t("loadBalancers.service.cookieNameHint")}
                />
                <TextField
                  label={t("loadBalancers.service.cookieLifetime")}
                  name="cookieLifetime"
                  inputMode="numeric"
                  value={draft.cookieLifetime}
                  onChange={(e) => set({ cookieLifetime: e.target.value })}
                  placeholder="300"
                  error={err("cookieLifetime")}
                />
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Problems the API found in fields this form has no input for (or names by
 * a nested path): shown together, each with its translated reason.
 */
export function FieldProblems({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!(error instanceof ApiProblem) || !error.problem?.fields?.length) return null;
  return (
    <Alert tone="danger">
      {error.problem.fields.map((f) => (
        <p key={f.path.join(".")}>
          {f.reason
            ? t(`errors.reasons.${f.reason}`, { defaultValue: t("errors.fieldInvalid") })
            : t("errors.fieldInvalid")}
        </p>
      ))}
    </Alert>
  );
}

/** Adds a service, or changes the one `service` names. */
export function ServiceDialog({
  projectId,
  lb,
  service,
  onClose,
}: {
  projectId: string;
  lb: LoadBalancer;
  service: LoadBalancerService | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const add = useAddService(projectId, lb.id);
  const update = useUpdateService(projectId, lb.id);
  const certificates = useCertificates(projectId).data ?? [];
  const [draft, setDraft] = useState(() => draftOf(service ?? undefined));
  const [tried, setTried] = useState(false);
  const mutation = service ? update : add;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (!draftValid(draft)) return;
    mutation.mutate(inputOf(draft), {
      onSuccess: () => {
        toast({
          tone: "ok",
          title: t(service ? "loadBalancers.service.saving" : "loadBalancers.service.adding", {
            listen: draft.listenPort,
            destination: draft.destinationPort,
          }),
        });
        onClose();
      },
    });
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={
        service
          ? t("loadBalancers.service.editTitle", { port: service.listenPort })
          : t("loadBalancers.service.addTitle")
      }
      description={t("loadBalancers.service.lead")}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="lb-service" busy={mutation.isPending}>
            {service ? t("common.save") : t("loadBalancers.service.add")}
          </Button>
        </>
      }
    >
      <form id="lb-service" onSubmit={submit} noValidate className="flex flex-col gap-4">
        <ServiceFields
          projectId={projectId}
          draft={draft}
          onChange={setDraft}
          showErrors={tried}
          fixedPort={service !== null}
          certificates={certificates}
        />
        {tried && !draftValid(draft) && <Alert tone="danger">{t("loadBalancers.fixErrors")}</Alert>}
        <FieldProblems error={mutation.error} />
        <ErrorMessage error={mutation.error} />
      </form>
    </Dialog>
  );
}
